import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Webcam from "react-webcam";
import { FaCalendarCheck, FaWhatsapp } from "react-icons/fa";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { SlCallOut } from "react-icons/sl";
import styles from "../CreateAgentComponent/CreateAgentComponent.module.css";
import LoaderSpiner from "../LoaderSpiner";

const languages = [
  { value: "en-IN", name: "English (India)" },
  { value: "en-US", name: "English (United States)" },
  { value: "en-GB", name: "English (United Kingdom)" },
  { value: "hi", name: "Hindi" },
  { value: "ar", name: "Arabic" },
  { value: "ru", name: "Russian" },
];

const EditAgentComponent = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const webcamRef = useRef(null);

  const [avatarName, setAvatarName] = useState("");
  const [avatarPrefix, setAvatarPrefix] = useState("");
  const [avatarImage, setAvatarImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [voice, setVoice] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [model] = useState("Ultimate");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.3);
  const [firstSpeaking, setFirstSpeaking] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [maxDuration, setMaxDuration] = useState("");
  const [timeExceedMessage, setTimeExceedMessage] = useState("");
  const [transcript, setTranscript] = useState(false);
  const [callRecording, setCallRecording] = useState(false);

  const [bookCalendar, setBookCalendar] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [callTransfer, setCallTransfer] = useState(false);
  const [calcomToken, setCalcomToken] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [callTransferNumber, setCallTransferNumber] = useState("");

  const [showCalcomPopup, setShowCalcomPopup] = useState(false);
  const [showWhatsappPopup, setShowWhatsappPopup] = useState(false);
  const [showCallTransferPopup, setShowCallTransferPopup] = useState(false);
  const [showAvatarPanel, setShowAvatarPanel] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [numberError, setNumberError] = useState("");

  const [voices, setVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [voiceError, setVoiceError] = useState("");

  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptError, setPromptError] = useState("");

  const [corpora, setCorpora] = useState([]);
  const [isLoadingCorpora, setIsLoadingCorpora] = useState(false);
  const [corpusError, setCorpusError] = useState("");

  const [isLoadingAgent, setIsLoadingAgent] = useState(true);
  const [agentError, setAgentError] = useState("");

  const [corpusList, setCorpusList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const voicesPerPage = 5;

  const [savedPromptId, setSavedPromptId] = useState("");

  useEffect(() => {
    if (!agentId || agentId === "undefined") {
      setAgentError("Invalid agent ID");
      setIsLoadingAgent(false);
      return;
    }

    const fetchAgent = async () => {
      setIsLoadingAgent(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`https://prod.toxboxai.com/api/agent-creation/by-id/${agentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch agent");
        const data = await response.json();
        const agent = data.agent;

        const fullName = agent.avatarName || "";
        const [prefix = "", name = ""] = fullName.includes("-") ? fullName.split("-", 2) : ["", fullName];
        setAvatarPrefix(prefix);
        setAvatarName(name || fullName);

        setImagePreview(agent.avatarImage || "");
        setLanguage(agent.language || "");
        setGender(agent.gender || "");
        setVoice(agent.voice || "");
        setPhoneNumber(agent.phoneNumber || "");

        const promptId = agent.systemPrompt?._id || agent.systemPrompt || "";
        setSavedPromptId(promptId);

        setTemperature(agent.temperature || 0.3);
        setFirstSpeaking(agent.firstSpeaking || false);
        setWelcomeMessage(agent.firstSpeakerSettings?.agent?.welcomeMessage || "");
        setMaxDuration(agent.maxDuration ? agent.maxDuration.replace("s", "") : "");
        setTimeExceedMessage(agent.timeExceedMessage || "");
        setTranscript(agent.transcript || false);
        setCallRecording(agent.callRecording || false);

        setBookCalendar(agent.bookCalendar || false);
        setWhatsapp(agent.whatsapp || false);
        setCallTransfer(agent.callTransfer || false);
        setCalcomToken(agent.calcomToken || "");
        setWhatsappNumber(agent.whatsappNumber || "");
        setCallTransferNumber(agent.callTransferNumber || "");

        if (Array.isArray(agent.corpusList) && agent.corpusList.length > 0) {
          setCorpusList(agent.corpusList);
        }
      } catch (err) {
        setAgentError(err.message);
        toast.error(err.message);
      } finally {
        setIsLoadingAgent(false);
      }
    };
    fetchAgent();
  }, [agentId]);

  useEffect(() => {
    const fetchPrompts = async () => {
      setIsLoadingPrompts(true);
      try {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        const res = await fetch(
          `https://prod.toxboxai.com/api/participant/assign-prompts-by-salesperson/${userId}/outbound`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to load prompts");
        const data = await res.json();
        const promptList = Array.isArray(data.prompts) ? data.prompts : [];
        setPrompts(promptList);

        if (savedPromptId && promptList.length > 0) {
          const found = promptList.find(p => p._id === savedPromptId);
          if (found) {
            setSystemPrompt(savedPromptId);
          } else {
            console.warn("Saved prompt ID not found in list:", savedPromptId);
          }
        }
      } catch (err) {
        setPromptError("Failed to load prompts");
        toast.error("Failed to load prompts");
      } finally {
        setIsLoadingPrompts(false);
      }
    };
    fetchPrompts();
  }, [savedPromptId]);

  useEffect(() => {
    const fetchNumbers = async () => {
      setIsLoadingNumbers(true);
      try {
        const token = localStorage.getItem("token");
        const authData = JSON.parse(localStorage.getItem("authData") || "{}");
        const vendorId = authData?.user?.vendorId;
        if (!vendorId) throw new Error("Vendor ID missing");

        const res = await fetch(`https://prod.toxboxai.com/api/numbers/get-numbers-by-user/${vendorId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setPhoneNumbers(Array.isArray(data.numbers) ? data.numbers : []);
      } catch (err) {
        setNumberError(err.message);
      } finally {
        setIsLoadingNumbers(false);
      }
    };
    fetchNumbers();
  }, []);

  useEffect(() => {
    const fetchVoices = async () => {
      setIsLoadingVoices(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("https://prod.toxboxai.com/api/voice/get-voice-by-display-name", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setVoices(Array.isArray(data) ? data : []);
      } catch (err) {
        setVoiceError(err.message);
      } finally {
        setIsLoadingVoices(false);
      }
    };
    fetchVoices();
  }, []);

  useEffect(() => {
    const fetchCorpora = async () => {
      setIsLoadingCorpora(true);
      try {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        const res = await fetch(
          `https://prod.toxboxai.com/api/vendorknowledge/get-assigned-corpus/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setCorpora(Array.isArray(data.results) ? data.results : []);
      } catch (err) {
        setCorpusError(err.message);
      } finally {
        setIsLoadingCorpora(false);
      }
    };
    fetchCorpora();
  }, []);

  const handleFileUpload = (e) => {
    if (e.target.files[0]) {
      setAvatarImage(e.target.files[0]);
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setShowAvatarPanel(false);
    }
  };

  const handleAvatarSelect = (imgUrl) => {
    setAvatarImage(imgUrl);
    setImagePreview(imgUrl);
    setShowAvatarPanel(false);
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setAvatarImage(imageSrc);
    setImagePreview(imageSrc);
    setShowCamera(false);
  };

  const handleAddCorpus = () => {
    setCorpusList([
      ...corpusList,
      { corpus_id: "", nameOverride: "", descriptionOverride: "", max_results: 5 },
    ]);
  };

  const handleCorpusChange = (index, field, value) => {
    const updated = [...corpusList];
    updated[index][field] = field === "max_results" ? Number(value) : value;

    if (field === "corpus_id") {
      if (value === "gera") {
        updated[index].nameOverride = "searchGera";
        updated[index].descriptionOverride = "Search Gera-related property facts (location, budget, amenities, possession dates)";
      } else if (value === "codename") {
        updated[index].nameOverride = "SearchCodename";
        updated[index].descriptionOverride = "Search Codename-related property facts (location, budget, amenities, possession dates)";
      }
    }
    setCorpusList(updated);
  };

  const handleRemoveCorpus = (index) => {
    setCorpusList(corpusList.filter((_, i) => i !== index));
  };

  const handleVoiceSelect = (displayName) => {
    setVoice(displayName);
    toast.success(`Selected voice: ${displayName}`);
  };

  const filteredVoices = gender
    ? voices.filter((v) => v.gender?.toLowerCase() === gender.toLowerCase() || v.gender === "Unknown")
    : voices;

  const indexOfLastVoice = currentPage * voicesPerPage;
  const indexOfFirstVoice = indexOfLastVoice - voicesPerPage;
  const currentVoices = filteredVoices.slice(indexOfFirstVoice, indexOfLastVoice);
  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);

  const paginate = (page) => setCurrentPage(page);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = localStorage.getItem("userId");

    const payload = {
      avatarName: avatarPrefix ? `${avatarPrefix}-${avatarName}` : avatarName,
      avatarImage: typeof avatarImage === "string" ? avatarImage : "",
      language,
      gender,
      voice,
      phoneNumber,
      model: "Ultimate",
      systemPrompt,
      corpusList,
      temperature,
      firstSpeaking,
      firstSpeakerSettings: firstSpeaking ? { agent: { welcomeMessage } } : { user: {} },
      maxDuration: maxDuration ? `${maxDuration}s` : "",
      timeExceedMessage,
      transcript,
      callRecording,
      type: "outbound",
      bookCalendar,
      whatsapp,
      callTransfer,
      calcomToken,
      whatsappNumber,
      callTransferNumber,
      createdBy: userId,
    };

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`https://prod.toxboxai.com/api/agent-creation/update/${agentId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(data.message || "Agent updated successfully!");
        navigate("/outbound");
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Network error while updating agent");
    }
  };

  if (isLoadingAgent) return <LoaderSpiner />;
  if (agentError) return <div className={styles.error}>{agentError}</div>;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.headerWrap}>
          <h1 className={styles.header}>Edit Outbound Agent</h1>
        </div>

        <form className={styles.formCard} onSubmit={handleSubmit}>
          <div className={styles.gridWrap}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Basic Information</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Avatar Name *</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={avatarPrefix ? `${avatarPrefix}-${avatarName}` : avatarName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const [prefix = "", name = ""] = val.includes("-") ? val.split("-", 2) : ["", val];
                      setAvatarPrefix(prefix);
                      setAvatarName(name || val);
                    }}
                  />
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Avatar Image</label>
                  <div className={styles.uploadWrap}>
                    <div className={styles.uploadLabel} onClick={() => setShowAvatarPanel(true)}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className={styles.avatarPreview} />
                      ) : (
                        <span className={styles.uploadIcon}>Upload Image</span>
                      )}
                    </div>
                  </div>

                  {showAvatarPanel && (
                    <div className={styles.sidePanelOverlay} onClick={() => setShowAvatarPanel(false)}>
                      <div className={styles.sidePanel} onClick={(e) => e.stopPropagation()}>
                        <h3>Choose Avatar</h3>
                        <div className={styles.avatarGrid}>
                          {["/avatar1.jpg", "/avatar2.jpg", "/avatar3.jpg", "/avatar4.jpg", "/avatar5.png"].map((img) => (
                            <img key={img} src={img} alt="" className={styles.avatarOption} onClick={() => handleAvatarSelect(img)} />
                          ))}
                        </div>
                        <label className={styles.uploadButton}>
                          Upload <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                        </label>
                        <button type="button" className={styles.cameraButton} onClick={() => setShowCamera(true)}>
                          Capture with Camera
                        </button>
                        {showCamera && (
                          <div className={styles.cameraOverlay}>
                            <div className={styles.cameraContainer}>
                              <Webcam audio={false} ref={webcamRef} screenshotFormat="image/jpeg" className={styles.cameraFeed} />
                              <div className={styles.cameraButtons}>
                                <button onClick={capturePhoto} className={styles.captureButton}>Capture</button>
                                <button onClick={() => setShowCamera(false)} className={styles.cancelButton}>Cancel</button>
                              </div>
                            </div>
                          </div>
                        )}
                        <button type="button" className={styles.cancelButton} onClick={() => setShowAvatarPanel(false)}>
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Language *</label>
                  <select className={styles.input} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="">Select language</option>
                    {languages.map((l) => (
                      <option key={l.value} value={l.value}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Phone Number *</label>
                  <select className={styles.input} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}>
                    <option value="">{isLoadingNumbers ? "Loading..." : "Select number"}</option>
                    {phoneNumbers.map((p) => (
                      <option key={p._id} value={p.phoneNumber}>{p.phoneNumber}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Gender</label>
                  <select className={styles.input} value={gender} onChange={(e) => { setGender(e.target.value); setCurrentPage(1); }}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Voice Selection *</h2>
              {isLoadingVoices ? <LoaderSpiner /> : voiceError ? <p>{voiceError}</p> : (
                <>
                  <table className={styles.voiceTable}>
                    <thead><tr><th>Display Name</th><th>Preview</th><th>Action</th></tr></thead>
                    <tbody>
                      {currentVoices.length > 0 ? currentVoices.map((v) => (
                        <tr key={v._id}>
                          <td>{v.displayName}</td>
                          <td>
                            <audio controls className={styles.audioPreview}>
                              <source src={`https://prod.toxboxai.com${v.previewUrl}`} type="audio/mpeg" />
                            </audio>
                          </td>
                          <td>
                            <button type="button" className={styles.selectButton} onClick={() => handleVoiceSelect(v.displayName)}
                              disabled={voice === v.displayName}>
                              {voice === v.displayName ? "Selected" : "Select"}
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="3">No voices available</td></tr>
                      )}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
                      <span>{currentPage} of {totalPages}</span>
                      <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Agent Configuration</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Model</label>
                  <select className={styles.input} value={model} disabled>
                    <option value="Ultimate">Ultimate (Active)</option>
                  </select>
                  <small style={{ color: "#22c55e", fontWeight: "bold", marginTop: "6px", display: "block" }}>
                    Only Ultimate model is currently available
                  </small>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>System Prompt *</label>
                  <select className={styles.input} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)}>
                    <option value="">{isLoadingPrompts ? "Loading prompts..." : "Select prompt"}</option>
                    {prompts.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.promptName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Knowledge Bases</label>
                  <button type="button" className={styles.submitBtn} onClick={handleAddCorpus}>Add Knowledge Base</button>
                  {corpusList.map((item, idx) => (
                    <div key={idx} className={styles.corpusItem}>
                      <select
                        value={item.corpus_id}
                        onChange={(e) => handleCorpusChange(idx, "corpus_id", e.target.value)}
                        className={styles.input}
                      >
                        <option value="">Select corpus</option>
                        {corpora.map((c) => (
                          <option key={c.corpusId} value={c.corpusId}>{c.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Name Override"
                        value={item.nameOverride}
                        onChange={(e) => handleCorpusChange(idx, "nameOverride", e.target.value)}
                        className={styles.input}
                      />
                      <textarea
                        rows={2}
                        placeholder="Description Override"
                        value={item.descriptionOverride}
                        onChange={(e) => handleCorpusChange(idx, "descriptionOverride", e.target.value)}
                        className={styles.textarea}
                      />
                      <input
                        type="number"
                        min="1"
                        max="20"
                        placeholder="Max results"
                        value={item.max_results}
                        onChange={(e) => handleCorpusChange(idx, "max_results", e.target.value)}
                        className={styles.input}
                      />
                      <button type="button" className={styles.cancelBtn} onClick={() => handleRemoveCorpus(idx)}>Remove</button>
                    </div>
                  ))}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Temperature (0.1 - 1.0) *</label>
                  <div className={styles.sliderWrapper}>
                    <input type="range" min="0.1" max="1.0" step="0.1" value={temperature} onChange={(e) => setTemperature(Number(e.target.value))} className={styles.slider} />
                    <span className={styles.sliderValue}>{temperature}</span>
                  </div>
                </div>

                <div className={styles.toggleCard}>
                  <div className={styles.toggleLabelWrap}>
                    <span className={styles.label}>First Speaking:</span>
                    <div className={styles.toggleOptions}>
                      <span className={!firstSpeaking ? styles.activeToggle : styles.inactiveToggle}>User</span>
                      <span className={firstSpeaking ? styles.activeToggle : styles.inactiveToggle}>Agent</span>
                    </div>
                    <label className={styles.switch}>
                      <input type="checkbox" checked={firstSpeaking} onChange={() => setFirstSpeaking(!firstSpeaking)} />
                      <span className={styles.sliderSwitch}></span>
                    </label>
                  </div>
                  {firstSpeaking && (
                    <div className={styles.fieldWrap}>
                      <label className={styles.label}>Welcome Message</label>
                      <textarea rows={4} className={styles.textarea} value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} />
                    </div>
                  )}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Max Duration (seconds) *</label>
                  <input type="number" className={styles.input} value={maxDuration} onChange={(e) => setMaxDuration(e.target.value)} />
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Time Exceed Message *</label>
                  <input type="text" className={styles.input} value={timeExceedMessage} onChange={(e) => setTimeExceedMessage(e.target.value)} />
                </div>

                <div className={styles.toggleRow}>
                  <span className={styles.label}>Enable Transcript</span>
                  <label className={styles.switch}>
                    <input type="checkbox" checked={transcript} onChange={() => setTranscript(!transcript)} />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>

                <div className={styles.toggleRow}>
                  <span className={styles.label}>Enable Call Recording</span>
                  <label className={styles.switch}>
                    <input type="checkbox" checked={callRecording} onChange={() => setCallRecording(!callRecording)} />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Tools</h2>
              <div className={styles.toolGroup}>
                <button type="button" className={styles.toolButton} onClick={() => alert("End Call triggered")}>
                  <HiOutlinePhoneMissedCall /> End Call
                </button>
                <button type="button" className={styles.toolButton} onClick={() => setShowCalcomPopup(true)}>
                  <FaCalendarCheck /> Book Calendar
                </button>
                <button type="button" className={styles.toolButton} onClick={() => setShowCallTransferPopup(true)}>
                  <SlCallOut /> Call Transfer
                </button>
                <button type="button" className={styles.toolButton} onClick={() => setShowWhatsappPopup(true)}>
                  <FaWhatsapp /> WhatsApp
                </button>
              </div>

              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable Book Calendar</span>
                <label className={styles.switch}>
                  <input type="checkbox" checked={bookCalendar} onChange={() => setBookCalendar(!bookCalendar)} />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>
              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable WhatsApp</span>
                <label className={styles.switch}>
                  <input type="checkbox" checked={whatsapp} onChange={() => setWhatsapp(!whatsapp)} />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>
              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable Call Transfer</span>
                <label className={styles.switch}>
                  <input type="checkbox" checked={callTransfer} onChange={() => setCallTransfer(!callTransfer)} />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>

              {showCalcomPopup && (
                <div className={styles.popupOverlay} onClick={() => setShowCalcomPopup(false)}>
                  <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                    <h3>Cal.com API Key</h3>
                    <input type="text" value={calcomToken} onChange={(e) => setCalcomToken(e.target.value)} className={styles.input} placeholder="Enter API key" />
                    <div className={styles.popupButtons}>
                      <button className={styles.submitBtn} onClick={() => setShowCalcomPopup(false)}>Save</button>
                      <button className={styles.cancelBtn} onClick={() => setShowCalcomPopup(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {showCallTransferPopup && (
                <div className={styles.popupOverlay} onClick={() => setShowCallTransferPopup(false)}>
                  <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                    <h3>Call Transfer Number</h3>
                    <input type="text" value={callTransferNumber} onChange={(e) => setCallTransferNumber(e.target.value)} className={styles.input} placeholder="+1234567890" />
                    <div className={styles.popupButtons}>
                      <button className={styles.submitBtn} onClick={() => setShowCallTransferPopup(false)}>Save</button>
                      <button className={styles.cancelBtn} onClick={() => setShowCallTransferPopup(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {showWhatsappPopup && (
                <div className={styles.popupOverlay} onClick={() => setShowWhatsappPopup(false)}>
                  <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
                    <h3>WhatsApp Number</h3>
                    <input type="text" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} className={styles.input} placeholder="+1234567890" />
                    <div className={styles.popupButtons}>
                      <button className={styles.submitBtn} onClick={() => setShowWhatsappPopup(false)}>Save</button>
                      <button className={styles.cancelBtn} onClick={() => setShowWhatsappPopup(false)}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.buttonRow}>
            <button type="submit" className={styles.submitBtn}>Update Outbound Agent</button>
          </div>
        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default EditAgentComponent;